import React from 'react';
import {
  FieldValues,
  useController,
  Path,
  useFormContext,
} from 'react-hook-form';
import FieldError from './FieldError';
import { twJoin } from 'tailwind-merge';
import FieldLabel from './FieldLabel';
import FieldDescribe from './FieldDescribe';

type ControlledInputProps<TFormValues extends FieldValues> = {
  name: Path<TFormValues>;
  label: string | React.ReactNode;
  isRequired?: boolean;
  labelClassName?: string;
  describe?: React.ReactNode;
} & React.InputHTMLAttributes<HTMLInputElement>;

const ONLY_NUMBER = /\D/g;
const WITHOUT_DOT = /(\..*?)\..*/g;

export const onInputOnlyNumber = (
  event: React.ChangeEvent<HTMLInputElement>,
) => {
  const formatValue = event.target.value
    .replace(ONLY_NUMBER, '')
    .replace(WITHOUT_DOT, '$1');

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  event.target.value = formatValue || null;
};

const FormNumberInput = <TFormValues extends FieldValues>({
  name,
  label,
  className,
  describe,
  isRequired,
  disabled,
  labelClassName,
  ...props
}: ControlledInputProps<TFormValues>) => {
  const { control } = useFormContext<TFormValues>();
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  return (
    <div className="w-full flex flex-col gap-0.5">
      <FieldLabel
        name={name}
        className={labelClassName}
        label={label}
        isRequired={isRequired}
      />
      <div
        className={twJoin('flex flex-col gap-0.5', disabled && 'opacity-50')}
      >
        <input
          onInput={onInputOnlyNumber}
          {...field}
          {...props}
          disabled={disabled}
          id={name}
          className={twJoin(
            className,
            'border border-med-gray-3 font-medium text-sm h-12 placeholder-med-gray-3 placeholder:font-medium outline-none focus:border-blue-600 rounded-5 py-1.5 px-3',
            error && 'border-alert-negative bg-error-yellow',
          )}
        />
        {error && (
          <FieldError className="!whitespace-normal">
            {error?.message}
          </FieldError>
        )}
        {!error && describe && <FieldDescribe>{describe}</FieldDescribe>}
      </div>
    </div>
  );
};

export default FormNumberInput;
